// La foto que el usuario sube al atajo "Editar" —
// ADR-2026-10-22-editar-una-foto-subida-con-la-misma-edicion.
//
// La edición de las creaciones (el visor con la barra de Editar en escritorio,
// `EditarImagen` en el celular) trabaja sobre una FILA de `imagenes_generadas`: la abre por
// id, edita por `imagen_id`, arma la cadena de versiones y el escritor parte de ella. Para
// que una foto subida use exactamente esa edición, se guarda como fila OCULTA:
//   • `active: false` → no aparece en Creaciones ni en las grillas (filtran `active`). Las
//     versiones editadas sí, porque la edición las activa como a cualquiera.
//   • `modo_utilizado: 'foto_subida'` y 0 créditos → no es una generación de Atlas; las
//     métricas que cuentan filas la tienen que excluir por ese modo.
// El archivo va a `generadas/{empresa}/full/` (más su `opt/`), donde el visor y el backend
// buscan el original de cualquier creación.

import { supabase } from './supabase';
import { UnsupportedImageFormatError, normalizeImageForUpload } from '../utils/heicConvert';
import { setCachedSignedUrl } from '../utils/imageCache';
import { makeThumbnail } from '../pages/edicionGrafica/export/saveToHistory';
import type { FilaInicialVisor } from '../pages/GeneratedImagesHistory';

const GENERADAS_BUCKET = 'generadas';
const FIRMA_TTL = 3600;

/** El `modo_utilizado` de estas filas: lo que las distingue de una generación. */
export const MODO_FOTO_SUBIDA = 'foto_subida';

/** Los formatos con los que se marcan las filas (los que entiende la cadena de edición). */
const FORMATOS: { nombre: string; ratio: number }[] = [
  { nombre: '9:16', ratio: 9 / 16 },
  { nombre: '4:5', ratio: 4 / 5 },
  { nombre: '2:3', ratio: 2 / 3 },
  { nombre: '3:4', ratio: 3 / 4 },
  { nombre: '1:1', ratio: 1 },
  { nombre: '4:3', ratio: 4 / 3 },
  { nombre: '3:2', ratio: 3 / 2 },
  { nombre: '16:9', ratio: 16 / 9 },
  { nombre: '21:9', ratio: 21 / 9 },
];

/** El formato más cercano a las medidas: la edición lo hereda para no cambiar el encuadre. */
export function formatoMasCercano(ancho: number, alto: number): string | null {
  if (!ancho || !alto) return null;
  const r = ancho / alto;
  let mejor = FORMATOS[0];
  for (const f of FORMATOS) if (Math.abs(f.ratio - r) < Math.abs(mejor.ratio - r)) mejor = f;
  return mejor.nombre;
}

async function medir(blob: Blob): Promise<{ w: number; h: number } | null> {
  try {
    const bmp = await createImageBitmap(blob);
    const medidas = { w: bmp.width, h: bmp.height };
    bmp.close();
    return medidas;
  } catch {
    return null;
  }
}

/**
 * Normaliza (HEIC/AVIF → JPEG, rechaza RAW/TIFF), sube la foto a `generadas` y crea su
 * fila oculta. Devuelve la fila lista para abrir el visor sin ir a la red. Lanza `Error`
 * con mensaje mostrable.
 */
export async function guardarFotoParaEditar(file: File, companyId: string): Promise<FilaInicialVisor> {
  let foto: File;
  try {
    foto = await normalizeImageForUpload(file);
  } catch (err) {
    if (err instanceof UnsupportedImageFormatError) throw new Error(err.message);
    throw new Error('No pudimos procesar esa imagen. Probá con otra.');
  }

  const ext = (foto.name.split('.').pop() || 'jpg').toLowerCase();
  const fileName = `${crypto.randomUUID()}.${ext}`;
  const fullPath = `${companyId}/full/${fileName}`;
  const optPath = `${companyId}/opt/${fileName}`;

  const { error: upErr } = await supabase.storage
    .from(GENERADAS_BUCKET)
    .upload(fullPath, foto, { contentType: foto.type || 'image/jpeg', upsert: false });
  if (upErr) throw new Error('No se pudo subir la imagen. Probá de nuevo.');

  // La miniatura es best-effort: sin ella el visor cae al original.
  const miniatura = await makeThumbnail(foto);
  if (miniatura) {
    await supabase.storage
      .from(GENERADAS_BUCKET)
      .upload(optPath, miniatura, { contentType: 'image/png', upsert: false })
      .then(
        () => undefined,
        () => undefined,
      );
  }

  const medidas = await medir(foto);
  const formato = medidas ? formatoMasCercano(medidas.w, medidas.h) : null;
  const {
    data: { user },
  } = await supabase.auth.getUser();
  const datosGeneracion = { origen: MODO_FOTO_SUBIDA, nombre_archivo: file.name };
  const { data: fila, error: insErr } = await supabase
    .from('imagenes_generadas')
    .insert({
      id_company: companyId,
      user_id: user?.id ?? null,
      file_name: fileName,
      tipo: 'imagen',
      formato,
      active: false,
      modo_utilizado: MODO_FOTO_SUBIDA,
      creditos_gastados: 0,
      datos_generacion: datosGeneracion,
    })
    .select('id, created_at')
    .single();
  if (insErr || !fila) {
    await supabase.storage.from(GENERADAS_BUCKET).remove([fullPath, optPath]).then(
      () => undefined,
      () => undefined,
    );
    throw new Error('No se pudo preparar la imagen para editarla. Probá de nuevo.');
  }

  const { data: firmas } = await supabase.storage
    .from(GENERADAS_BUCKET)
    .createSignedUrls(miniatura ? [fullPath, optPath] : [fullPath], FIRMA_TTL);
  const firmaFull = firmas?.[0]?.signedUrl;
  if (!firmaFull) throw new Error('No se pudo preparar la imagen subida.');
  const firmaOpt = (miniatura && firmas?.[1]?.signedUrl) || firmaFull;
  setCachedSignedUrl(fullPath, firmaFull);
  if (miniatura) setCachedSignedUrl(optPath, firmaOpt);

  const { id, created_at } = fila as { id: string; created_at: string };
  return {
    id,
    file_name: fileName,
    created_at,
    user_id: user?.id ?? null,
    variante_id: null,
    variantes_ids: [],
    datos_generacion: datosGeneracion,
    variante_nombre: 'Imagen',
    formato: formato ?? '',
    tipo: 'imagen',
    favorito: false,
    upscaled_path: null,
    thumbnailUrl: firmaOpt,
    signedUrl: firmaFull,
  };
}
